class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this._seq = 0;
    this.lockZoomWhileTyping();
    this.watchContents();
    this.syncEmptyState();
  }

  /* The host <cart-drawer> carries an "is-empty" class that hides the header and squashes the
     item list. It must always match what is really rendered inside, otherwise the drawer shows
     blank / without products. Single source of truth = presence of the empty-state markup. */
  syncEmptyState() {
    const hasItems = !!this.querySelector('tr.cart-item');
    const showsEmpty = !!this.querySelector('.drawer__inner-empty');
    const isEmpty = showsEmpty && !hasItems;
    this.classList.toggle('is-empty', isEmpty);
    const inner = this.querySelector('.drawer__inner');
    if (inner && !isEmpty) inner.classList.remove('is-empty');
  }

  /* App-like typing: while a field inside the drawer has focus, the page cannot zoom
     (iOS / Android auto-zoom). Pinch-zoom is restored as soon as the field loses focus. */
  lockZoomWhileTyping() {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    const original = meta.getAttribute('content');
    const isField = (el) => el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) && !/^(checkbox|radio|button)$/.test(el.type || '');
    document.addEventListener('focusin', (e) => {
      if (isField(e.target) && e.target.closest('cart-drawer, .nv-cp, cart-items')) meta.setAttribute('content', original.replace(/,?\s*maximum-scale=[^,]*/g, '') + ',maximum-scale=1');
    });
    document.addEventListener('focusout', (e) => {
      if (isField(e.target) && e.target.closest('cart-drawer, .nv-cp, cart-items')) setTimeout(() => {
        if (!isField(document.activeElement)) meta.setAttribute('content', original);
      }, 50);
    });
  }

  watchContents() {
    if (this._observer || !window.MutationObserver) return;
    let queued = false;
    this._observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        this.syncEmptyState();
      });
    });
    this._observer.observe(this, { childList: true, subtree: true });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.syncEmptyState();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  refreshFromServer(attempt = 0) {
    // used when something calls renderContents() without the add-to-cart response.
    // Latest request wins, so a slow older response can never overwrite a newer cart.
    const seq = ++this._seq;
    const root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
    const sections = this.getSectionsToRender().map((section) => section.id).join(',');
    return fetch(`${root}?sections=${sections}&_=${Date.now()}`, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('cart-drawer refresh failed');
        return response.json();
      })
      .then((sectionsData) => {
        if (seq !== this._seq) return;
        if (!this.paintSections(sectionsData)) throw new Error('cart-drawer section missing');
        this.openSoon();
      })
      .catch((error) => {
        console.error(error);
        if (seq === this._seq && attempt < 2) {
          return new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1))).then(() =>
            this.refreshFromServer(attempt + 1)
          );
        }
      });
  }

  renderContents(parsedState) {
    if (!parsedState || !parsedState.sections) return this.refreshFromServer();
    this._seq++;
    this.productId = parsedState.id;
    if (!this.paintSections(parsedState.sections)) return this.refreshFromServer();
    this.openSoon();
  }

  /* Writes the fresh section HTML into the drawer. Returns false if anything is missing,
     so the caller can re-fetch instead of leaving the drawer blank. */
  paintSections(sectionsData) {
    if (!sectionsData) return false;
    let painted = false;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const html = sectionsData[section.id];
      if (!sectionElement || !html) return;
      const inner = this.getSectionInnerHTML(html, section.selector);
      if (inner === null) return;
      sectionElement.innerHTML = inner;
      if (section.id === 'cart-drawer') {
        painted = true;
        // copy the empty / not-empty state from the fresh markup onto the host element
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
        if (fresh) this.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
      }
    });
    this.syncEmptyState();
    return painted;
  }

  openSoon() {
    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay && !overlay.dataset.nvBound) {
        overlay.dataset.nvBound = '1';
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
